<script setup lang="ts">
import { omit } from 'lodash-es';
import { computed, toRef } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import type { ITrackerDealForm, ITrackerDealFormComposableOptions, ITrackerDealFormSchema } from '@modules/affiliates-approved/submodules/trackers-settings/composables';
import type { ITrackersModalPageCommonProps } from '@modules/affiliates-approved/submodules/trackers-settings/submodules/trackers-list/types';
import type { IChangeHistoryEntry } from '@modules/change-history/components/VChangeHistory/types';
import VCard from '@components/card/VCard/VCard.vue';
import VLoader from '@components/loader/VLoader.vue';
import {

  useTrackerDealForm,
} from '@modules/affiliates-approved/submodules/trackers-settings/composables';
import { TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS } from '@modules/affiliates-approved/submodules/trackers-settings/constants';
import {
  AFFILIATES_APPROVED_TRACKERS_LIST_PERMISSIONS,
  TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_TRACKERS,
} from '@modules/affiliates-approved/submodules/trackers-settings/submodules/trackers-list/constants';
import { useAffiliateShowCurrentTrackerQuery } from '@modules/affiliates-approved/submodules/trackers-settings/submodules/trackers-list/queries';
import VChangeHistory from '@modules/change-history/components/VChangeHistory/VChangeHistory.vue';
import { useFormatMonthDate } from '@modules/core/composables/datetime';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { EFormInputTypes } from '@modules/forms/enums';
import { useI18n } from '@modules/i18n/composables';
import { useMerchantIdStore } from '@modules/merchant/store';
import { useQueryClient } from '@tanstack/vue-query';
import { isString } from '@utils/guards';

import { useAffiliateCurrentTrackerUpdateMutation, useAffiliateTrackerHistoryDeleteMutation } from '../../mutations';
import TrackerHistoryChangesEditPage from '../../submodules/tracker-history-changes-edit/pages/TrackerHistoryChangesEditPage/TrackerHistoryChangesEditPage.vue';

const props = defineProps<ITrackersModalPageCommonProps>();

const route = useRoute();
const router = useRouter();
const merchantId = toRef(useMerchantIdStore(), 'merchantId');
const { formatMonthDate } = useFormatMonthDate();
const { getTranslation } = useI18n();
const queryClient = useQueryClient();

const affiliateId = computed(() => (isString(route.params.id) ? Number(route.params.id) : 0));
const trackerId = computed(() => (isString(route.params.trackerId) ? Number(route.params.trackerId) : 0));
const itemId = computed(() => (isString(route.params.itemId) ? Number(route.params.itemId) : 0));

const { data, isLoading, setData } = useAffiliateShowCurrentTrackerQuery(affiliateId, trackerId);

const {
  mutateAsync,
  data: updateHistoryResponse,
  isLoading: isUpdateLoading,
  errors,
  clearError,
} = useAffiliateCurrentTrackerUpdateMutation();

const { mutateAsync: removeTrackerHistory } = useAffiliateTrackerHistoryDeleteMutation();
const currentDate = new Date();

const formOptions = computed<ITrackerDealFormComposableOptions>(() => ({
  conditions: data.value?.conditions.all_condition ?? [],
  isLoading: isLoading.value,
  conditionValue: data.value?.tracker.condition_id,
  conditionLabel: getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_TRACKERS, 'SETTINGS_TRACKERS_FORM_CONDITION').value,
  dateLabel: getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_TRACKERS, 'SETTINGS_TRACKERS_FORM_DATE').value,
  dateValue: currentDate,
}));

const historyOfTrackerChanges = computed<IChangeHistoryEntry[] | undefined>(() => data.value?.tracker.history_of_trackers_changes?.map((item) => ({
  ...item,
  active: itemId.value === item.id,
})));

const selectedHistoryItem = computed<IChangeHistoryEntry | undefined>(() => historyOfTrackerChanges.value?.find((item) => item.active));

const { formSchema } = useTrackerDealForm(formOptions);

const schema = computed<ITrackerDealFormSchema>(() => {
  const form: ITrackerDealFormSchema = {
    merchant_name: {
      type: EFormInputTypes.TEXT,
      label: getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_TRACKERS, 'SETTINGS_TRACKERS_MERCHANT_NAME').value,
      value: data.value?.tracker.merchant_name,
      readonly: true,
    },
    serial_id: {
      type: EFormInputTypes.NUMBER,
      label: getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_TRACKERS, 'SETTINGS_TRACKERS_FORM_TRACKER').value,
      value: data.value?.tracker.tracker_code,
      useGrouping: false,
      maxFractionDigits: 0,
      readonly: true,
    },
    ...formSchema.value,
  };

  return form;
});

async function onSubmit({ condition_id, date }: ITrackerDealForm): Promise<void> {
  try {
    await mutateAsync({
      id: affiliateId.value,
      trackerId: trackerId.value,
      tracker: {
        ...omit(data.value?.tracker, ['history_of_trackers_changes']),
        condition_id,
        date: formatMonthDate(date),
      },
    });

    updateHistoryOfTrackerChanges(updateHistoryResponse.value?.history_of_trackers_changes);

    if (props.trackersListQueryKey) {
      await queryClient.invalidateQueries({ queryKey: [props.trackersListQueryKey] });
    }
  } catch {}
}

function onHistoryEdit(entry: IChangeHistoryEntry): void {
  void router.push({ name: props.editHistoryRoute.name, params: { merchantId: merchantId.value, itemId: entry.id } });
}

function updateHistoryOfTrackerChanges(list?: IChangeHistoryEntry[]): void {
  setData((queryData) => {
    if (!queryData) {
      return queryData;
    }

    return {
      ...queryData,
      tracker: {
        ...queryData.tracker,
        history_of_trackers_changes: list ?? queryData.tracker.history_of_trackers_changes,
      },
    };
  });
}

async function onHistoryDelete(entry: IChangeHistoryEntry): Promise<void> {
  try {
    await removeTrackerHistory(entry.id);

    updateHistoryOfTrackerChanges(historyOfTrackerChanges.value?.filter(({ id }) => id !== entry.id));

    if (itemId.value === entry.id) {
      await router.replace(props.backFromEditHistoryRoute);
    }
  } catch {}
}
</script>

<template>
  <VCard v-data-qa-auto-id="'TrackerDealChangesPage'"
    type="form"
    class="rounded-b-none"
    :title="$getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS, 'TRACKER_SETTINGS_HEADER')"
  >
    <VForm
      :schema="schema"
      :submit-btn-label="$getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS, 'TRACKER_SETTINGS_SAVE')"
      :readonly="!$hasPermission(AFFILIATES_APPROVED_TRACKERS_LIST_PERMISSIONS.HISTORY_CHANGES_EDIT)"
      :is-loading="isUpdateLoading"
      :custom-errors="errors"
      @submit="onSubmit"
      @interacted="clearError"
    />
  </VCard>
  <div class="rounded-b-xl border-t bg-content-color">
    <VLoader :loading="isLoading">
      <VChangeHistory
        class="rounded-t-none"
        :list="historyOfTrackerChanges"
        :edit-permission="AFFILIATES_APPROVED_TRACKERS_LIST_PERMISSIONS.HISTORY_CHANGES_EDIT"
        :delete-permission="AFFILIATES_APPROVED_TRACKERS_LIST_PERMISSIONS.HISTORY_CHANGES_DELETE"
        @edit="onHistoryEdit"
        @delete="onHistoryDelete"
      >
        <TrackerHistoryChangesEditPage
          :back-from-edit-history-route="backFromEditHistoryRoute"
          :merchant-name="selectedHistoryItem?.merchant_name"
        />
      </VChangeHistory>
    </VLoader>
  </div>
</template>
